/* Rule suggestions for one dataset, over the real API. No `X-Lumiq-Tenant` header — hard rule 1.
 *
 * Two hooks and one mutation, and the mutation is a **PUT**. `ai-rulegen.yaml` explains why at
 * length: the first draft was a POST arguing it needed no `Idempotency-Key` because it is idempotent
 * by natural key, and the `post-idempotency-key` gate refused the build — correctly, since hard rule 7
 * does not take an argument. Regenerating *replaces* the dataset's suggestion set with what the
 * current schema justifies, so the verb was the honest answer rather than an exemption.
 *
 * `put` did not exist in `shared/api/http` and is added there rather than inlined here, so the next
 * PUT surface (the sample policy, `ai/serving/route`) has one to reach for instead of a second fetch
 * call with its own header spelling.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import {
  isApplyOutcome,
  isAutoSuggestPreview,
  isAutoSuggestResult,
  isDismissOutcome,
  isGenerationOutcome,
  isMatchingRules,
  isRuleDraft,
  isRulegenCapabilities,
  isSuggestionCounts,
  isSuggestionList,
} from '@/shared/api/generated/ai-rulegen';
import type {
  ApplyOutcome,
  AutoSuggestPreview,
  AutoSuggestResult,
  DismissOutcome,
  GenerationOutcome,
  MatchingRules,
  RuleDraft,
  RulegenCapabilities,
  SuggestionCounts,
  SuggestionList,
} from '@/shared/api/generated/ai-rulegen';
import {
  forgetIdempotencyKey,
  getOne,
  idempotencyKeyFor,
  post,
  put,
} from '@/shared/api/http';

export const CAPABILITIES_URL = '/v1/ai/rulegen/capabilities';
export const COUNTS_URL = '/v1/ai/rulegen/suggestion-counts';

/* Re-exported so the screen and the presentation model import from one place, the same seam every
 * other feature slice keeps: this module is where a wire type enters the feature. */
export type {
  AppliedRule,
  ApplyOutcome,
  AutoSuggestItem,
  AutoSuggestOutcome,
  AutoSuggestPreview,
  AutoSuggestRecommendation,
  AutoSuggestResult,
  AutoSuggestSummary,
  RecommendedAction,
  CandidateSource,
  CoverageGap,
  ExistingRule,
  MatchingRules,
  RuleCoverage,
  DatasetSuggestionCount,
  GenerationOutcome,
  GenerationRun,
  ModelStatus,
  ObservationKind,
  Origin,
  ProfileObservation,
  ProfileStatus,
  RejectionReason,
  RuleDraft,
  RulegenCapabilities,
  Suggestion,
  SuggestionCounts,
  SuggestionList,
  SuggestionStatus,
} from '@/shared/api/generated/ai-rulegen';

function suggestionsUrl(datasetId: string): string {
  return `/v1/ai/rulegen/datasets/${encodeURIComponent(datasetId)}/suggestions`;
}

export function useRulegenCapabilities(): UseQueryResult<RulegenCapabilities> {
  return useQuery({
    queryKey: ['rulegen', 'capabilities'],
    queryFn: () =>
      getOne(CAPABILITIES_URL, isRulegenCapabilities, 'rulegen capabilities'),
    /* Five minutes. The generator's own boundaries change when the deployment changes, not while a
     * steward is reading — and `never_proposed` is a security argument this screen renders, so it
     * should not blink between refetches. */
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export function useSuggestions(
  datasetId: string | undefined,
): UseQueryResult<SuggestionList> {
  return useQuery({
    queryKey: ['rulegen', 'suggestions', datasetId],
    queryFn: () =>
      getOne(suggestionsUrl(datasetId ?? ''), isSuggestionList, 'suggestions'),
    enabled: datasetId !== undefined,
    retry: false,
  });
}

/** How many suggestions each dataset has. **One request for the estate, never one per row.**
 *
 * The alternative is a fetch per catalogue row, and an N+1 is why a badge like this gets cut from a
 * design rather than shipped. A dataset with no suggestions is *absent* from the answer rather than
 * present with `0` — the contract says why, and a consumer must read a missing key as "not
 * generated for" rather than as "generated and found nothing".
 *
 * Five minutes stale, matching `useRulegenCapabilities`: a badge is ambient information beside
 * something a reader came for, and one that flickers between refetches is worse than one slightly
 * behind. Regeneration invalidates it, so the screen that *caused* a change sees it at once.
 */
export function useSuggestionCounts(): UseQueryResult<SuggestionCounts> {
  return useQuery({
    queryKey: ['rulegen', 'counts'],
    queryFn: () => getOne(COUNTS_URL, isSuggestionCounts, 'suggestion counts'),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/** The field values a rule form is prefilled with. **A GET, so it creates nothing.**
 *
 * §2's loop runs through this rather than through an accept route: a suggestion becomes a rule only
 * by a person submitting `POST /v1/rules`, and this hook supplies what that form starts with. The
 * draft carries `tags` holding `suggested-from:<id>`, which is what lets the *second* hop — the
 * version's columns and parameters — be recovered after a reload rather than surviving only in
 * router state.
 *
 * `enabled` on a present id, so `RuleDetail` may call it unconditionally for a rule that turns out
 * to carry no provenance tag. Never retried: a 404 means the suggestion was regenerated away, which
 * is a real answer and not a transient one.
 */
export function useRuleDraft(
  suggestionId: string | undefined,
): UseQueryResult<RuleDraft> {
  return useQuery({
    queryKey: ['rulegen', 'draft', suggestionId],
    queryFn: () =>
      getOne(
        `/v1/ai/rulegen/suggestions/${encodeURIComponent(suggestionId ?? '')}/rule-draft`,
        isRuleDraft,
        'rule draft',
      ),
    enabled: suggestionId !== undefined && suggestionId !== '',
    retry: false,
  });
}

/** Regenerates, and returns the outcome — which carries what the plain list cannot.
 *
 * `GenerationOutcome` holds `by_source`, `rejected`, `columns_considered`,
 * `unbounded_numeric_columns` and the run's `model_status`; `SuggestionList` holds only the
 * suggestions. That asymmetry is the contract's and it is right — those numbers describe *a run*,
 * not a dataset's current state, so serving them from the GET would attribute the last
 * generation's counts to whatever the list holds now, and would report "AI contributed" for a run
 * some earlier caller performed. The screen therefore keeps the outcome from the regeneration it
 * performed and does not pretend to have one before.
 */
export function useRegenerateSuggestions(): UseMutationResult<
  GenerationOutcome,
  Error,
  string
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (datasetId: string) =>
      put(suggestionsUrl(datasetId), isGenerationOutcome, 'generation outcome', {}),
    onSuccess: (outcome) => {
      void client.invalidateQueries({
        queryKey: ['rulegen', 'suggestions', outcome.dataset_id],
      });
      /* The badge count is stale for five minutes by design, and a regeneration is the one event
         that must not wait: a reader who has just generated and sees "3 suggested" beside a dataset
         that now has seven would reasonably conclude the run did not take. */
      void client.invalidateQueries({ queryKey: ['rulegen', 'counts'] });
    },
    retry: false,
  });
}

/** Active rules already in the library that could serve one recommendation, best fit first.
 *
 * Fetched only while the apply panel is open — `enabled` is the panel being open, so a list of
 * fifty recommendations does not issue fifty reads nobody asked for. */
export function useMatchingRules(
  suggestionId: string,
  enabled: boolean,
): UseQueryResult<MatchingRules> {
  return useQuery({
    queryKey: ['rulegen', 'matching-rules', suggestionId],
    queryFn: () =>
      getOne(
        `/v1/ai/rulegen/suggestions/${encodeURIComponent(suggestionId)}/matching-rules`,
        isMatchingRules,
        'matching rules',
      ),
    enabled,
    retry: false,
  });
}

/** What a person sets when applying. Everything else is the recommendation's own.
 *
 * With `existingRuleId` the recommendation is served by that already-approved rule, bound at
 * `severity`; the name, parameter and activation fields only apply to drafting a new rule. */
export interface ApplyChoice {
  readonly suggestionId: string;
  readonly datasetId: string;
  readonly name?: string;
  readonly parameters?: Readonly<Record<string, string>>;
  readonly requestActivation: boolean;
  readonly existingRuleId?: string;
  readonly severity?: string;
}

/** After any decision: the list (its statuses and rule progress), the estate badge, and the rules
 * screens — applying created a rule there, and binding changed what a dataset is checked against. */
function refreshAfterDecision(
  client: ReturnType<typeof useQueryClient>,
  datasetId: string,
): void {
  void client.invalidateQueries({ queryKey: ['rulegen', 'suggestions', datasetId] });
  void client.invalidateQueries({ queryKey: ['rulegen', 'counts'] });
  void client.invalidateQueries({ queryKey: ['rules'] });
}

/** Authors a **draft** rule from a recommendation. Activation still needs two other people.
 *
 * **The key is per recommendation, and kept until it succeeds.** A double-click or a retry after a
 * lost response replays the same key — and the server is idempotent on the recommendation anyway,
 * answering `replayed` with the rule it already made. Forgotten on success, so applying a
 * recommendation that a later regeneration re-offered is a new request rather than a replay.
 */
export function useApplySuggestion(): UseMutationResult<
  ApplyOutcome,
  Error,
  ApplyChoice
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (choice: ApplyChoice) =>
      post(
        `/v1/ai/rulegen/suggestions/${encodeURIComponent(choice.suggestionId)}/apply`,
        idempotencyKeyFor('rulegen.apply', choice.suggestionId),
        isApplyOutcome,
        'applied recommendation',
        {
          ...(choice.name !== undefined && choice.name.trim() !== ''
            ? { name: choice.name.trim() }
            : {}),
          ...(choice.parameters !== undefined ? { parameters: choice.parameters } : {}),
          request_activation: choice.requestActivation,
          ...(choice.existingRuleId !== undefined
            ? {
                existing_rule_id: choice.existingRuleId,
                severity: choice.severity ?? 'warning',
              }
            : {}),
        },
      ),
    onSuccess: (_outcome, choice) => {
      forgetIdempotencyKey('rulegen.apply', choice.suggestionId);
      refreshAfterDecision(client, choice.datasetId);
      void client.invalidateQueries({ queryKey: ['rulegen', 'matching-rules'] });
    },
    retry: false,
  });
}

export interface BindChoice {
  readonly suggestionId: string;
  readonly datasetId: string;
  readonly severity: string;
}

/** Binds the activated rule to its dataset. 409 until two people have activated it. */
export function useBindSuggestion(): UseMutationResult<ApplyOutcome, Error, BindChoice> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (choice: BindChoice) =>
      post(
        `/v1/ai/rulegen/suggestions/${encodeURIComponent(choice.suggestionId)}/bind`,
        idempotencyKeyFor('rulegen.bind', choice.suggestionId),
        isApplyOutcome,
        'bound recommendation',
        { severity: choice.severity },
      ),
    onSuccess: (_outcome, choice) => {
      forgetIdempotencyKey('rulegen.bind', choice.suggestionId);
      refreshAfterDecision(client, choice.datasetId);
    },
    retry: false,
  });
}

export interface DismissChoice {
  readonly suggestionId: string;
  readonly datasetId: string;
  readonly reason: string;
}

/** Declines a recommendation, with a reason. Held until the dataset's schema version moves. */
export function useDismissSuggestion(): UseMutationResult<
  DismissOutcome,
  Error,
  DismissChoice
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (choice: DismissChoice) =>
      post(
        `/v1/ai/rulegen/suggestions/${encodeURIComponent(choice.suggestionId)}/dismiss`,
        idempotencyKeyFor('rulegen.dismiss', choice.suggestionId),
        isDismissOutcome,
        'dismissed recommendation',
        { reason: choice.reason },
      ),
    onSuccess: (_outcome, choice) => {
      forgetIdempotencyKey('rulegen.dismiss', choice.suggestionId);
      refreshAfterDecision(client, choice.datasetId);
    },
    retry: false,
  });
}

/** Recommends rules for one dataset and says what putting each in place would take. **Binds
 * nothing.** The only write is the regeneration, the same one `useRegenerateSuggestions` makes, so
 * the dataset's recommendation list and badge are refreshed for it.
 *
 * The key is per dataset and kept until it succeeds: a retry after a lost response replays it. A
 * second click while one is in flight is refused by the server with a 409 as well as by the busy
 * button, because a disabled button is not a guarantee.
 */
export function useAutoSuggestPreview(): UseMutationResult<
  AutoSuggestPreview,
  Error,
  string
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (datasetId: string) =>
      post(
        `/v1/ai/rulegen/datasets/${encodeURIComponent(datasetId)}/auto-suggest`,
        idempotencyKeyFor('rulegen.auto-suggest', datasetId),
        isAutoSuggestPreview,
        'recommended rules',
        {},
      ),
    onSuccess: (_preview, datasetId) => {
      forgetIdempotencyKey('rulegen.auto-suggest', datasetId);
      void client.invalidateQueries({ queryKey: ['rulegen', 'suggestions', datasetId] });
      void client.invalidateQueries({ queryKey: ['rulegen', 'counts'] });
    },
    retry: false,
  });
}

export interface BindRecommendedChoice {
  readonly datasetId: string;
  readonly suggestionIds: readonly string[];
}

/** Binds the selected recommendations, each to a rule two people already approved. Never drafts.
 *
 * **Keyed on the selection**, sorted, so a double-click or a retry of the same selection replays
 * one key — and the server is idempotent by state anyway: a recommendation already bound reports
 * `already_bound` and gains no second binding.
 */
export function useBindRecommended(): UseMutationResult<
  AutoSuggestResult,
  Error,
  BindRecommendedChoice
> {
  const client = useQueryClient();
  const keyOf = (choice: BindRecommendedChoice): string =>
    `${choice.datasetId}:${[...choice.suggestionIds].sort().join(',')}`;
  return useMutation({
    mutationFn: (choice: BindRecommendedChoice) =>
      post(
        `/v1/ai/rulegen/datasets/${encodeURIComponent(choice.datasetId)}/auto-suggest/bind`,
        idempotencyKeyFor('rulegen.auto-suggest.bind', keyOf(choice)),
        isAutoSuggestResult,
        'bound recommendations',
        { suggestion_ids: [...choice.suggestionIds] },
      ),
    onSuccess: (_result, choice) => {
      forgetIdempotencyKey('rulegen.auto-suggest.bind', keyOf(choice));
      /* The bindings the Rules tab lists, and this dataset's recommendations and badge — the same
         refresh any single decision makes. */
      refreshAfterDecision(client, choice.datasetId);
    },
    retry: false,
  });
}
